import React, { useState, useEffect, useMemo } from 'react';
import { Users, Check, AlertCircle, Loader2 } from 'lucide-react';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import { subscriptionService, PaymentInitRequest } from '@/services/subscription.service';
import { getDiscountPercent, getPriceMultiplier } from '@/utils/discounts';
import { useToast } from '@/hooks/use-toast';
import MobileModal from '@/components/shared/MobileModal';

interface PremiumModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubscribe: () => void;
}

const PremiumModal: React.FC<PremiumModalProps> = ({ isOpen, onClose, onSubscribe }) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const { toast } = useToast();
  
  // State for billing cycle and discount
  const [selectedBillingCycle, setSelectedBillingCycle] = useState<'quarterly' | 'biannual' | 'annual'>('quarterly');
  const [discountCode, setDiscountCode] = useState<string>('');
  const [showDiscountCode, setShowDiscountCode] = useState(false);
  const [isValidatingDiscount, setIsValidatingDiscount] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [discountValidation, setDiscountValidation] = useState<{
    isValid: boolean;
    discount?: any;
    discountAmount?: number;
    finalAmount?: number;
    error?: string;
  } | null>(null);

  // Base price per month
  const basePrice = 13500;

  // Re-validate discount when billing cycle changes
  useEffect(() => {
    if (discountCode && discountValidation?.isValid) {
      validateDiscountCode(discountCode);
    }
  }, [selectedBillingCycle]);

  // Calculate billing options with discounts
  const billingOptions = useMemo(() => {
    const options = {
      quarterly: { 
        price: basePrice * 3, 
        total: basePrice * 3,
        label: 'Quarterly',
        period: '3 months',
        discount: 0,
        perMonth: basePrice
      },
      biannual: { 
        price: basePrice * 6, 
        total: Math.round((basePrice * 6) * getPriceMultiplier('biannual')),
        label: 'Bi-Annual',
        period: '6 months',
        discount: getDiscountPercent('biannual'),
        perMonth: Math.round((basePrice * 6) * getPriceMultiplier('biannual')) / 6
      },
      annual: { 
        price: basePrice * 12, 
        total: Math.round((basePrice * 12) * getPriceMultiplier('annual')),
        label: 'Annual',
        period: '12 months',
        discount: getDiscountPercent('annual'),
        perMonth: Math.round((basePrice * 12) * getPriceMultiplier('annual')) / 12
      }
    };

    // Apply discount code if valid
    if (discountValidation?.isValid && discountValidation.finalAmount) {
      const selectedOption = options[selectedBillingCycle];
      selectedOption.total = discountValidation.finalAmount;
      selectedOption.perMonth = Math.round(discountValidation.finalAmount / (selectedBillingCycle === 'quarterly' ? 3 : selectedBillingCycle === 'biannual' ? 6 : 12));
    }

    return options;
  }, [discountValidation, selectedBillingCycle]);

  // Validate discount code
  const validateDiscountCode = async (code: string) => {
    if (!code.trim()) {
      setDiscountValidation(null);
      return;
    }

    setIsValidatingDiscount(true);
    try {
      const planId = 'plan_' + selectedBillingCycle;
      const response = await subscriptionService.validateDiscountCode(code.trim(), planId);
      
      if (response.success && response.data && response.data.isValid) {
        const currentOption = billingOptions[selectedBillingCycle];
        const discountPercentage = response.data.discountPercentage || 0;
        const calculatedDiscountAmount = Math.round(currentOption.total * (discountPercentage / 100));
        const calculatedFinalAmount = currentOption.total - calculatedDiscountAmount;
        
        setDiscountValidation({
          isValid: true,
          discount: response.data,
          discountAmount: response.data.discountAmount || calculatedDiscountAmount,
          finalAmount: response.data.finalAmount || calculatedFinalAmount,
        });
        
        toast({
          title: 'Discount Applied!',
          description: `${discountPercentage}% discount has been applied to your ${selectedBillingCycle} plan.`,
        });
      } else {
        setDiscountValidation({
          isValid: false,
          error: response.success ? response.data?.message || 'Invalid or expired discount code' : 'Failed to validate discount code',
        });
        
        toast({
          title: 'Invalid Code',
          description: (response.success && response.data?.message) || 'The discount code you entered is not valid.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Discount validation error:', error);
      setDiscountValidation({
        isValid: false,
        error: 'Failed to validate discount code. Please try again.',
      });
    } finally {
      setIsValidatingDiscount(false);
    }
  };

  // Handle payment initialization
  const handlePayment = async () => {
    if (!user) {
      console.error('User not authenticated');
      return;
    }

    setIsProcessingPayment(true);
    
    try {
      const planId = 'plan_' + selectedBillingCycle;
      const paymentData: PaymentInitRequest = {
        planId,
        email: user.email,
        name: user.fullName || user.email,
        phone: user.phone,
        discountCode: discountCode || undefined,
        callbackUrl: `${window.location.origin}/account/billing/payment/callback`
      };

      const result = await subscriptionService.initializePayment(paymentData);
      
      if (result.success && result.data) {
        // Store reference for callback handling
        localStorage.setItem('paymentReference', result.data.reference);
        
        // Call the original onSubscribe callback
        onSubscribe();
        
        // Redirect to Paystack
        window.location.href = result.data.authorizationUrl;
      } else {
        console.error('Payment initialization failed');
        toast({
          title: 'Payment Failed',
          description: 'Failed to initialize payment. Please try again.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Payment initialization error:', error);
      toast({
        title: 'Payment Error',
        description: 'An error occurred while processing your payment. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsProcessingPayment(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <>
      {/* Title */}
      <h2 className="text-2xl font-bold text-gray-900 mb-2">
        Try Premium
      </h2>
      <p className="text-gray-600 text-sm mb-6">
        Unlock unlimited posts, verified badge, and more
      </p>

      {/* Features List */}
      <div className="mb-6 space-y-3 text-left">
        {/* Collaboration Feature - Highlighted */}
        <div className="flex items-start gap-3 p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-100">
          <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
            <Users size={16} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900">
              Free Collaboration Posts (3x/week)
            </p>
            <p className="text-xs text-gray-600 mt-0.5">
              Grow your professional fashion brand with collaboration opportunities
            </p>
          </div>
        </div>

        {/* Other Features */}
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 w-5 h-5 bg-gray-900 rounded-full flex items-center justify-center">
            <Check size={12} className="text-white" />
          </div>
          <p className="text-sm text-gray-700">Unlimited portfolio posts</p>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 w-5 h-5 bg-gray-900 rounded-full flex items-center justify-center">
            <Check size={12} className="text-white" />
          </div>
          <p className="text-sm text-gray-700">Verified badge on profile</p>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 w-5 h-5 bg-gray-900 rounded-full flex items-center justify-center">
            <Check size={12} className="text-white" />
          </div>
          <p className="text-sm text-gray-700">Priority support</p>
        </div>
      </div>

      {/* Billing Cycle Selection - Mobile Only */}
      <div className="sm:hidden mb-6">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Choose Billing Cycle</h4>
        <div className="space-y-2">
          {Object.entries(billingOptions).map(([key, option]) => (
            <button
              key={key}
              onClick={() => setSelectedBillingCycle(key as 'quarterly' | 'biannual' | 'annual')}
              className={`w-full p-3 rounded-xl border-2 transition-all text-left ${
                selectedBillingCycle === key
                  ? 'border-gray-900 bg-gray-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold text-gray-900">{option.label}</span>
                {option.discount > 0 && (
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-semibold">
                    Save {option.discount}%
                  </span>
                )}
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-lg font-bold text-gray-900">
                  ₦{Math.round(option.perMonth).toLocaleString()}
                </span>
                <span className="text-sm text-gray-500">/month</span>
              </div>
              <p className="text-xs text-gray-600">
                Total: ₦{option.total.toLocaleString()} for {option.period}
              </p>
            </button>
          ))}
        </div>
      </div>

      {/* Discount Code Section - Mobile Only */}
      <div className="sm:hidden mb-6">
        {!showDiscountCode ? (
          <button
            onClick={() => setShowDiscountCode(true)}
            className="text-sm text-gray-600 hover:text-gray-900 font-medium underline"
          >
            Have a discount code?
          </button>
        ) : discountValidation?.isValid ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Check size={16} className="text-green-600" />
              <span className="text-sm text-green-700 font-semibold">
                {discountValidation.discount?.discountType === 'percentage' 
                  ? `${discountValidation.discount?.discountPercentage || 0}% discount applied!`
                  : `₦${(discountValidation.discountAmount || 0).toLocaleString()} discount applied!`
                }
              </span>
            </div>
            <button
              onClick={() => {
                setShowDiscountCode(false);
                setDiscountCode('');
                setDiscountValidation(null);
              }}
              className="text-green-600 hover:text-green-800"
            >
              ×
            </button>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-900">Discount Code</span>
              <button
                onClick={() => {
                  setShowDiscountCode(false);
                  setDiscountCode('');
                  setDiscountValidation(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <input
                  type="text"
                  placeholder="Enter discount code"
                  value={discountCode}
                  onChange={(e) => {
                    setDiscountCode(e.target.value);
                    setDiscountValidation(null);
                  }}
                  className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-colors text-sm ${
                    discountValidation?.error 
                      ? 'border-red-500 bg-red-50' 
                      : 'border-gray-200 focus:border-gray-900'
                  }`}
                />
                {isValidatingDiscount && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <Loader2 className="w-3 h-3 animate-spin text-gray-400" />
                  </div>
                )}
              </div>
              <button
                onClick={() => validateDiscountCode(discountCode)}
                disabled={!discountCode.trim() || isValidatingDiscount}
                className="px-3 py-2 bg-gray-900 hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-semibold transition-colors text-sm"
              >
                Apply
              </button>
            </div>
            {discountValidation?.error && (
              <div className="flex items-center gap-2 text-xs text-red-600 mt-2">
                <AlertCircle size={14} />
                <span>{discountValidation.error}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pricing - Updated for selected cycle */}
      <div className="mb-6">
        <div className="flex items-baseline justify-center gap-1 mb-1">
          <span className="text-4xl font-bold text-gray-900">
            ₦{Math.round(billingOptions[selectedBillingCycle].perMonth).toLocaleString()}
          </span>
          <span className="text-gray-500">/month</span>
        </div>
        <p className="text-sm text-gray-600 text-center">
          Total: ₦{billingOptions[selectedBillingCycle].total.toLocaleString()} for {billingOptions[selectedBillingCycle].period}
        </p>
        <p className="text-xs text-gray-500 text-center mt-1">Cancel anytime</p>
      </div>

      {/* CTA Button */}
      <button
        onClick={handlePayment}
        disabled={isProcessingPayment}
        className="w-full py-4 bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-2xl font-semibold hover:from-gray-800 hover:to-gray-700 disabled:from-gray-600 disabled:to-gray-600 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl mb-4 flex items-center justify-center gap-2"
      >
        {isProcessingPayment ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Processing...
          </>
        ) : (
          'Continue to Payment'
        )}
      </button>

    
    </>
  );

  return (
    <MobileModal
      isOpen={isOpen}
      onClose={onClose}
      contentClassName="text-center"
    >
      {modalContent}
    </MobileModal>
  );
};

export default PremiumModal;
